import type { FloorDef, RoomDef } from './types'

/** Physical stacking order, top to bottom, for the floor rail. */
export const floors: FloorDef[] = [
  { id: 'loft', name: 'Loft', level: 3 },
  { id: '2_etg', name: '2. etasje', level: 2 },
  { id: '1_etasje', name: '1. etasje', level: 1 },
  { id: 'kjeller', name: 'Kjeller', level: 0 },
  { id: 'ute', name: 'Ute', level: -1 },
]

/**
 * Rooms seeded from the live area/device/entity registries on the HA VM.
 * Includes dedicated per-room comfort sensors (temp/humidity/lux/occupancy),
 * the climate-zone mode select, the adaptive-lighting master switch, and the
 * room's per-device power where those exist. Only entities verified present
 * are listed; currently-unavailable ones stay (they flap) and render a
 * designed unavailable state.
 */
export const rooms: RoomDef[] = [
  // ── 1. etasje ───────────────────────────────────────────────
  {
    id: 'stua',
    name: 'Stua',
    floor: '1_etasje',
    icon: 'sofa',
    lights: ['light.stua_alle', 'light.stua_bord', 'light.stueled_shelly'],
    climate: 'climate.stua',
    media: ['media_player.stua_65', 'media_player.samsung_7_series_65'],
    vacuum: 'vacuum.roborock_de_260443705_s5',
    temperature: 'sensor.temp_stua_temperature',
    humidity: 'sensor.temp_stua_humidity',
    illuminance: 'sensor.stua_illuminance',
    occupancy: 'binary_sensor.mcp_stua_occupancy',
    zoneMode: 'input_select.climate_zone_mode_stua',
    adaptiveLighting: 'switch.adaptive_lighting_stua',
  },
  {
    id: 'kjokkenet',
    name: 'Kjøkkenet',
    floor: '1_etasje',
    icon: 'cooking-pot',
    lights: ['light.kjokkenet', 'light.kjokkenet_benk', 'light.kjokkenet_bord', 'light.kjokkenet_tak', 'light.kitchen_lights'],
    climate: 'climate.kjokkenet_thermostat',
    media: ['media_player.kjokkenet'],
    temperature: 'sensor.temp_kjokken_temperature',
    humidity: 'sensor.temp_kjokken_humidity',
    illuminance: 'sensor.kjokken_illuminance',
    occupancy: 'binary_sensor.mcp_kjokkenet_occupancy',
    zoneMode: 'input_select.climate_zone_mode_kjokkenet',
    adaptiveLighting: 'switch.adaptive_lighting_kjokkenet',
  },
  {
    id: 'vindfanget',
    name: 'Vindfanget',
    floor: '1_etasje',
    icon: 'door-open',
    lights: ['light.vindfanget_spot_1', 'light.vindfanget_spot_2', 'light.vindfanget_spot_3', 'light.vindfanget_spot_4'],
    climate: 'climate.floor_thermostat',
    occupancy: 'binary_sensor.mcp_vindfanget_occupancy',
    zoneMode: 'input_select.climate_zone_mode_vindfanget',
  },

  // ── 2. etasje ───────────────────────────────────────────────
  {
    id: 'hovedsoverom',
    name: 'Hovedsoverom',
    floor: '2_etg',
    icon: 'bed-double',
    lights: ['light.hovedsoverom_shelly'],
    climate: 'climate.masterbedroom_ovn',
    media: ['media_player.soverom'],
    temperature: 'sensor.temp_hovedsoverom_temperature',
    humidity: 'sensor.temp_hovedsoverom_humidity',
    illuminance: 'sensor.hovedsoverom_illuminance',
    occupancy: 'binary_sensor.mcp_hovedsoverom_occupancy',
    zoneMode: 'input_select.climate_zone_mode_hovedsoverom',
    adaptiveLighting: 'switch.adaptive_lighting_hovedsoverom',
    power: 'sensor.masterbedroom_ovn_current_power',
  },
  {
    id: 'ingeborgs_rom',
    name: 'Ingeborgs rom',
    floor: '2_etg',
    icon: 'bed',
    lights: ['light.ingeborg_taklys'],
    climate: 'climate.ingeborgs_ovn',
    media: ['media_player.ingeborgs_rom'],
    temperature: 'sensor.temp_ingeborg_temperature',
    humidity: 'sensor.temp_ingeborg_humidity',
    occupancy: 'binary_sensor.mcp_ingeborgs_rom_occupancy',
    zoneMode: 'input_select.climate_zone_mode_ingeborgs_rom',
  },
  {
    id: 'bad',
    name: 'Bad',
    floor: '2_etg',
    icon: 'bath',
    climate: 'climate.bad_2etg_thermostat',
    temperature: 'sensor.temp_bad_temperature',
    humidity: 'sensor.temp_bad_humidity',
    illuminance: 'sensor.bad_illuminance',
    occupancy: 'binary_sensor.mcp_bad_2_etasje_occupancy',
    zoneMode: 'input_select.climate_zone_mode_bad_2_etasje',
  },

  // ── Kjeller ─────────────────────────────────────────────────
  {
    id: 'kjellerstua',
    name: 'Kjellerstua',
    floor: 'kjeller',
    icon: 'sofa',
    climate: 'climate.kjellerstua',
    media: ['media_player.eiriks_rom'],
    temperature: 'sensor.temp_kjellerstue_temperature',
    humidity: 'sensor.temp_kjellerstue_humidity',
    occupancy: 'binary_sensor.mcp_kjellerstua_occupancy',
    zoneMode: 'input_select.climate_zone_mode_kjellerstua',
    power: 'sensor.kjellerstua_current_power',
  },
  {
    id: 'kontor_kjeller',
    name: 'Kontor',
    floor: 'kjeller',
    icon: 'briefcase',
    climate: 'climate.kjeller_kontor',
    occupancy: 'binary_sensor.basement_office_occupied',
    power: 'sensor.kjeller_kontor_current_power',
  },
  {
    id: 'bad_i_kjeller',
    name: 'Bad i kjeller',
    floor: 'kjeller',
    icon: 'bath',
    climate: 'climate.thermostat_bad_kjeller',
    occupancy: 'binary_sensor.mcp_bad_kjeller_occupancy',
    zoneMode: 'input_select.climate_zone_mode_bad_kjeller',
    power: 'sensor.bad_kjeller_floor_heating_power',
  },

  // ── Loft ────────────────────────────────────────────────────
  {
    id: 'loft',
    name: 'Loft',
    floor: 'loft',
    icon: 'warehouse',
    lights: [
      'light.loft_spot_1',
      'light.loft_spot_2',
      'light.loft_spot_3',
      'light.loft_spot_4',
      'light.loft_spot_5',
      'light.loft_desk',
    ],
    media: ['media_player.samsung_43_loftet'],
    temperature: 'sensor.temp_loft_temperature',
    humidity: 'sensor.temp_loft_humidity',
    illuminance: 'sensor.loft_illuminance',
    occupancy: 'binary_sensor.mcp_loft_occupancy',
    adaptiveLighting: 'switch.adaptive_lighting_loft',
  },

  // ── Ute / felles ────────────────────────────────────────────
  {
    id: 'ute',
    name: 'Utelys',
    floor: 'ute',
    icon: 'trees',
    lights: ['light.ute', 'light.ute_garasje', 'light.ute_inngang', 'light.ute_oppkjorsel', 'light.ute_hovedinngang'],
  },
  {
    id: 'garasjen',
    name: 'Garasjen',
    floor: 'ute',
    icon: 'car-front',
    lights: ['light.ute_garasje_hoyre', 'light.ute_garasje_venstre', 'light.garasjen_led'],
  },
  {
    id: 'trappegang',
    name: 'Trappegang',
    floor: 'ute',
    icon: 'footprints',
    lights: ['light.trappegang'],
  },
]
